import {Injectable} from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Observable, TimeoutError, throwError} from 'rxjs';
import {catchError, timeout} from 'rxjs/operators';
import {ErrorResponse} from '../error/error.model';

/**
 * Per-request override of the write bound (default 30000ms, following
 * the SKIP_RETRY idiom in http-error.interceptor.ts). PhotoService's
 * multipart writes raise it to 120000 so a healthy multi-photo upload
 * is not aborted, while activity writes keep the 30s acceptance
 * criterion (HEC-347).
 *
 * Usage:
 * ```ts
 * this.http.post(url, body, {
 *   context: new HttpContext().set(WRITE_TIMEOUT_MS, 120000)
 * });
 * ```
 */
export const WRITE_TIMEOUT_MS = new HttpContextToken<number>(() => 30000);

/** Shown (via the synthetic 504's body) when a write is aborted for taking too long. */
export const WRITE_TIMEOUT_MESSAGE =
  'The save is taking longer than expected. Please check your connection and try again.';

/**
 * Keycloak paths, mirroring the shape of SpinnerInterceptor.excludedPaths
 * (spinner.interceptor.ts). The token POST in particular must never see
 * a synthetic timeout: AuthService.handleUnauthorized() recurses through
 * it, and HttpErrorInterceptor already short-circuits that same URL for
 * exactly this reason.
 */
const KEYCLOAK_PATH_FRAGMENTS = ['/auth/', '/openid-connect/', '/realms/'];

/**
 * Bounds every non-GET request to WRITE_TIMEOUT_MS so a dead socket
 * cannot pin the spinner backdrop until the app is force-closed
 * (HEC-347). A TimeoutError is mapped to a synthetic HttpErrorResponse
 * (status 504, an ErrorResponse-shaped body) that HttpErrorInterceptor
 * reads exactly like a real server error - it toasts it, reports it to
 * Sentry, and never retries it (POST/PUT/PATCH/DELETE are excluded from
 * the retry policy regardless of status).
 *
 * Registration position (main.ts) is load-bearing: this interceptor
 * MUST sit between HttpErrorInterceptor and DirtyDataInterceptor.
 * DirtyDataInterceptor.isConnectionError treats `!navigator.onLine` or
 * `status === 0` as a connection error and mints a fake 200 so the
 * write is queued for later replay - a stalled write while online is
 * the common mobile scenario this ticket targets, and status 0 would
 * be swallowed there just as readily as it would be missed entirely
 * while offline. Using 504 (rather than 0) and registering here, where
 * DirtyDataInterceptor cannot see the request at all, keeps both halves
 * of that interceptor from converting the timeout into a silent
 * success - the exact class of bug this epic exists to remove.
 */
@Injectable()
export class WriteTimeoutInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method === 'GET' || this.isKeycloakPath(req.url)) {
      return next.handle(req);
    }

    const ms = req.context.get(WRITE_TIMEOUT_MS);
    return next.handle(req).pipe(
      timeout({each: ms}),
      catchError((error: unknown) => {
        if (error instanceof TimeoutError) {
          return throwError(() => this.toSyntheticTimeout(req));
        }
        return throwError(() => error);
      })
    );
  }

  private isKeycloakPath(url: string): boolean {
    return KEYCLOAK_PATH_FRAGMENTS.some(fragment => url.includes(fragment));
  }

  private toSyntheticTimeout(req: HttpRequest<any>): HttpErrorResponse {
    const body: ErrorResponse = {
      status: '504',
      message: WRITE_TIMEOUT_MESSAGE,
      detail: 'Client-side write timeout'
    };
    return new HttpErrorResponse({
      error: body,
      status: 504,
      statusText: 'Gateway Timeout',
      url: req.url
    });
  }
}

/** Shorthand for setting a request's write timeout override. */
export function withWriteTimeout(ms: number): HttpContext {
  return new HttpContext().set(WRITE_TIMEOUT_MS, ms);
}
